import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Card, 
  Form, 
  Input, 
  Button, 
  Divider, 
  message, 
  Tabs, 
  Switch,
  Upload,
  Avatar,
  Space
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined, 
  SaveOutlined,
  UploadOutlined,
  GoogleOutlined,
  GithubOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const ProfilePage = () => {
  const { currentUser, logout } = useAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(currentUser?.imageUrl || '');

  useEffect(() => {
    if (currentUser) {
      profileForm.setFieldsValue({
        username: currentUser.username,
        email: currentUser.email,
        fullName: currentUser.fullName || ''
      });
      
      setAvatarUrl(currentUser.imageUrl || '');
    }
  }, [currentUser, profileForm]);

  // 更新个人资料
  const handleProfileUpdate = async (values) => {
    setSaving(true);
    
    try {
      // 调用API更新用户资料
      // 这里示例，实际需要根据后端API实现
      await authApi.updateUserProfile(values);
      
      message.success('个人资料已更新');
    } catch (error) {
      console.error('更新个人资料失败:', error);
      message.error('更新个人资料失败');
    } finally {
      setSaving(false);
    }
  };

  // 更改密码
  const handlePasswordChange = async (values) => {
    setSaving(true);
    
    try {
      // 调用API更改密码
      // 这里示例，实际需要根据后端API实现
      await authApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      
      message.success('密码已更新');
      passwordForm.resetFields();
    } catch (error) {
      console.error('密码更新失败:', error);
      message.error('密码更新失败: ' + (error.response?.data?.message || '请检查当前密码是否正确'));
    } finally {
      setSaving(false);
    }
  };

  // 头像上传前检查
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('只能上传JPG/PNG格式图片!');
    }
    
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小不能超过2MB!');
    }
    
    return isJpgOrPng && isLt2M;
  };

  // 头像上传变化处理
  const handleAvatarChange = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    
    if (info.file.status === 'done') {
      setLoading(false);
      setAvatarUrl(info.file.response.imageUrl);
      message.success('头像上传成功!');
    } else if (info.file.status === 'error') {
      setLoading(false);
      message.error('头像上传失败.');
    }
  };

  // 删除账号确认
  const handleDeleteAccount = () => {
    // 这里只是示例，实际应该有确认对话框等
    message.info('此功能尚未实现');
  };

  // 关联社交账号
  const handleConnectOAuth = (provider) => {
    // 重定向到OAuth授权页面
    window.location.href = `/oauth2/authorization/${provider}?connect=true`;
  };

  return (
    <div className="container">
      <Title level={2}>个人设置</Title>
      <Paragraph style={{ marginBottom: '24px' }}>
        管理您的账号信息、密码和关联账号
      </Paragraph>
      
      <Tabs defaultActiveKey="profile">
        <TabPane 
          tab={
            <span>
              <UserOutlined />
              个人资料
            </span>
          } 
          key="profile"
        >
          <Card>
            <div style={{ display: 'flex', marginBottom: '24px' }}>
              <Upload
                name="avatar"
                listType="picture-card"
                showUploadList={false}
                action="/api/users/avatar"
                beforeUpload={beforeUpload}
                onChange={handleAvatarChange}
              >
                {avatarUrl ? (
                  <Avatar 
                    src={avatarUrl} 
                    size={100} 
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <div>
                    {loading ? <div>上传中...</div> : <UploadOutlined />}
                    <div style={{ marginTop: 8 }}>上传头像</div>
                  </div>
                )}
              </Upload>
              <div style={{ marginLeft: '20px' }}>
                <Paragraph>
                  <strong>用户名:</strong> {currentUser?.username}
                </Paragraph>
                <Paragraph>
                  <strong>账号创建时间:</strong> {currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleDateString('zh-CN') : '未知'}
                </Paragraph>
              </div>
            </div>
            
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileUpdate}
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  disabled 
                  placeholder="用户名"
                />
              </Form.Item>
              
              <Form.Item
                name="email"
                label="邮箱地址"
                rules={[
                  { required: true, message: '请输入邮箱地址' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="邮箱地址" 
                />
              </Form.Item>
              
              <Form.Item
                name="fullName"
                label="姓名"
              >
                <Input 
                  placeholder="您的真实姓名(可选)" 
                />
              </Form.Item>
              
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={saving}
                  icon={<SaveOutlined />}
                >
                  保存个人资料
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <LockOutlined />
              安全设置
            </span>
          } 
          key="security"
        >
          <Card title="修改密码">
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
            >
              <Form.Item
                name="currentPassword"
                label="当前密码"
                rules={[{ required: true, message: '请输入当前密码' }]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="当前密码"
                />
              </Form.Item>
              
              <Form.Item
                name="newPassword"
                label="新密码"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码长度至少为6个字符' }
                ]}
                hasFeedback
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="新密码"
                />
              </Form.Item>
              
              <Form.Item
                name="confirmPassword"
                label="确认新密码"
                dependencies={['newPassword']}
                hasFeedback
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不匹配'));
                    },
                  }),
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="确认新密码"
                />
              </Form.Item>
              
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={saving}
                >
                  更新密码
                </Button>
              </Form.Item>
            </Form>
          </Card>
          
          <Card title="关联账号" style={{ marginTop: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <Paragraph>
                关联社交媒体账号，使用这些账号进行登录
              </Paragraph>
            </div>
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Space>
                  <GoogleOutlined style={{ fontSize: '18px' }} />
                  <span>Google</span>
                </Space>
                <Button 
                  onClick={() => handleConnectOAuth('google')}
                >
                  {currentUser?.googleConnected ? '已关联' : '关联'}
                </Button>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <GithubOutlined style={{ fontSize: '18px' }} />
                  <span>GitHub</span>
                </Space>
                <Button 
                  onClick={() => handleConnectOAuth('github')}
                >
                  {currentUser?.githubConnected ? '已关联' : '关联'}
                </Button>
              </div>
            </div>
          </Card>
          
          <Card title="账号设置" style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>双因素认证</div>
                <Text type="secondary">启用双因素认证以增强账号安全</Text>
              </div>
              <Switch 
                checked={currentUser?.twoFactorEnabled} 
                onChange={() => message.info('此功能尚未实现')}
              />
            </div>
            
            <Divider />
            
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#f5222d' }}>危险区域</div>
              <Paragraph>
                删除您的账号将永久删除您的所有数据，此操作无法撤销。
              </Paragraph>
              <Button 
                danger 
                onClick={handleDeleteAccount}
              >
                删除我的账号
              </Button>
            </div>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ProfilePage; 